import type { ReactNode } from "react";
import { Link, useParams } from "react-router-dom";
import { LegalLinks } from "@app/components/LegalLinks.tsx";
import { useConsent } from "@app/state/consent.ts";

/** Reabre el aviso de cookies para cambiar la elección. */
function CookieSettings() {
  const reopen = useConsent((s) => s.reopen);
  return (
    <button type="button" className="cta cta--ghost" onClick={reopen}>
      Configurar cookies
    </button>
  );
}

const PAGES: Record<string, { title: string; body: ReactNode }> = {
  "aviso-legal": {
    title: "Aviso legal",
    body: (
      <>
        <p>
          MapaGol es un juego de trivia geográfica con fines de entretenimiento. No está
          afiliado, patrocinado ni asociado a ninguna liga, club, federación ni competición. Los
          nombres de lugares y competiciones se usan con fines informativos y descriptivos.
        </p>
        <p>
          Los datos cartográficos proceden de OpenStreetMap y Natural Earth. Consulta la página de
          atribuciones para más detalle.
        </p>
      </>
    ),
  },
  privacidad: {
    title: "Política de privacidad",
    body: (
      <>
        <p>Qué datos tratamos y con qué fin:</p>
        <ul>
          <li>
            <b>Identificador anónimo de jugador:</b> una cookie propia, firmada, que permite
            mantener tu partida, tu racha y tus estadísticas sin que crees una cuenta.
          </li>
          <li>
            <b>Partidas e intentos:</b> la ronda jugada, los puntos y la distancia, para calcular
            resultados, racha y percentiles.
          </li>
          <li>
            <b>Eventos de producto:</b> aperturas, inicios de ronda, compartidos y finalizaciones,
            asociados al identificador anónimo y a la ronda, para medir el funcionamiento del
            producto. No guardamos IP completa ni datos personales identificativos.
          </li>
          <li>
            <b>Ubicación aproximada:</b> nuestro proveedor (Cloudflare) indica la ciudad
            aproximada de cada conexión. La usamos solo para ordenar las pistas de la más cercana
            a la más lejana; guardamos ese orden con tu partida, pero no tu ubicación ni tu IP.
          </li>
          <li>
            <b>Origen de la visita:</b> si llegas desde un enlace con parámetros de campaña
            (<code>utm_source</code>, <code>utm_campaign</code>), guardamos durante 7 días en tu
            navegador la red de origen (TikTok, Instagram…) y el nombre de la campaña, y los
            registramos con tus eventos y tu partida. Solo los usamos de forma agregada, para saber
            qué vídeos traen jugadores; no contienen datos personales.
          </li>
          <li>
            <b>Medición de audiencia (opcional):</b> si aceptas el aviso de cookies, Google
            Analytics registra visitas y uso de forma agregada. Solo se activa con tu
            consentimiento, que puedes retirar cuando quieras.
          </li>
        </ul>
        <p>
          Base jurídica: ejecución del servicio e interés legítimo para las cookies necesarias, y
          tu consentimiento para la analítica. No usamos servicios de terceros que rastreen entre
          sitios con fines publicitarios. Puedes borrar tu identificador eliminando los datos del
          navegador.
        </p>
      </>
    ),
  },
  cookies: {
    title: "Política de cookies",
    body: (
      <>
        <p>MapaGol usa cookies propias. Estas son necesarias y no requieren consentimiento:</p>
        <ul>
          <li>
            <code>gf_pid</code>: cookie funcional firmada que te identifica de forma anónima para
            mantener tu partida y tu racha (hasta 400 días).
          </li>
          <li>
            <code>gf_ref</code>: conserva durante un día el resultado que te compartieron, para
            atribuir el compartido. También es funcional.
          </li>
        </ul>
        <p>
          Si aceptas el aviso, cargamos además <b>Google Analytics</b> (Google Ireland Limited)
          para medir las visitas de forma agregada. Instala las cookies <code>_ga</code> y{" "}
          <code>_ga_G-DJGSHB1KM9</code>, con una duración de hasta 2 años. No se cargan hasta que
          aceptas y puedes cambiar de opinión cuando quieras.
        </p>
        <CookieSettings />
      </>
    ),
  },
  atribuciones: {
    title: "Atribuciones",
    body: (
      <>
        <ul>
          <li>Datos cartográficos: © OpenStreetMap contributors (ODbL).</li>
          <li>Datos de límites y relieve: Natural Earth (dominio público).</li>
          <li>Tipografías: Big Shoulders Display, Chakra Petch y Barlow (SIL Open Font License).</li>
        </ul>
      </>
    ),
  },
};

export function Legal() {
  const { page } = useParams();
  const entry = page ? PAGES[page] : undefined;

  return (
    <section className="screen screen--scroll" aria-labelledby="legalTitle">
      <div className="legal">
        <h1 id="legalTitle">{entry ? entry.title : "Información legal"}</h1>
        {entry ? (
          entry.body
        ) : (
          <p>La página que buscas no existe.</p>
        )}
        <Link className="cta cta--ghost" to="/">
          Volver al inicio
        </Link>
        <LegalLinks />
      </div>
    </section>
  );
}
